'use client'
import { JSX, useState } from 'react'

import { useSearchParams } from 'next/navigation'

import { SignupPage } from '~/app/_root/SignupPage'
import { LoadingIndicator } from '~/components/LoadingIndicator'
import { Dialog } from '~/components/component-library/Dialog'
import { CreateWorkspace } from '~/components/create-workspace'
import { NewOrgModal } from '~/components/settings/NewOrgModal'
import { SmartAccountsList } from '~/components/smart-accounts/SmartAccountsList'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrgId } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { usePayeeInvitationRedirect } from '~/hooks/usePayeeInvitationRedirect'
import { useStuckLoadingDetection } from '~/hooks/useStuckLoadingDetection'

export function DashboardPage(): JSX.Element {
  const { user: authUser } = useAuthUser()
  const {
    onboarding: { isOnboarding, isDataLoaded },
  } = useAuth()
  const activeOrgId = useActiveOrgId()
  const searchParams = useSearchParams()

  const [isNewOrgModalOpen, setIsNewOrgModalOpen] = useState(false)
  const [hasNewOrgIntent, setHasNewOrgIntent] = useState(
    searchParams.get('newOrg') === 'true',
  )

  // Consume the one-time ?newOrg=true handoff only once onboarding state is
  // known: new users are already entering first-workspace onboarding, so only
  // existing users get the modal. Strip the param so logout/login can't
  // re-trigger it.
  if (hasNewOrgIntent && !!authUser && isDataLoaded) {
    setHasNewOrgIntent(false)
    if (!isOnboarding) setIsNewOrgModalOpen(true)
    const params = new URLSearchParams(searchParams)
    params.delete('newOrg')
    window.history.replaceState(
      null,
      '',
      params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname,
    )
  }

  // Redirect to payee onboarding if user has payee invitations and no orgs
  usePayeeInvitationRedirect()

  const isWaitingForData = !!authUser && !isDataLoaded

  useStuckLoadingDetection('DashboardPage', isWaitingForData, {
    extra: {
      hasAuthUser: !!authUser,
      activeOrgId,
      numOrgs: authUser?.orgs.length,
      orgIds: authUser?.orgs.map((o) => o.id),
    },
  })

  if (!authUser) return <SignupPage />
  if (!isDataLoaded) return <LoadingIndicator />
  if (isOnboarding) return <CreateWorkspace />

  return (
    <>
      <SmartAccountsList />
      <Dialog open={isNewOrgModalOpen} onOpenChange={setIsNewOrgModalOpen}>
        <NewOrgModal
          isModalOpen={isNewOrgModalOpen}
          setIsModalOpen={setIsNewOrgModalOpen}
        />
      </Dialog>
    </>
  )
}
